// Library imports
import React from 'react';
import { MDBDropdown, MDBIcon } from 'mdb-react-ui-kit';
import { arrayOf, bool, element, func, oneOfType, shape, string } from 'prop-types';
// Styled components
import { MenuContainer, MenuItem, Toggle, SectionHeader, ClearButton } from './styles';
// Util imports
import { SPORTS } from '../../utils/constants';

const isSectionHeader = item => {
    return typeof item === 'string' && item.match(/^[—\-]\s*.+\s*[—\-]$/);
};

const formatValue = value => {
    if (!value) {
        return 'Select...';
    }
    if (Array.isArray(value)) {
        if (value.length === 0) {
            return 'Select...';
        }
        if (value.length === 1) {
            return formatValue(value[0]);
        }
        return `${value.length} selected`;
    }
    if (typeof value === 'string' && value.includes(', ')) {
        const items = value.split(', ').filter(Boolean);
        if (items.length > 1) {
            return `${items.length} selected`;
        }
        return items[0] || 'Select...';
    }
    if (value === 'current_season') return 'Current Season';
    if (value === 'all_time') return 'All Time';
    if (SPORTS.includes(value)) return String(value).toUpperCase();
    if (value === 'all') return 'All';
    return value;
};

const Dropdown = ({
    containerStyle = {},
    dropdownStyle = {},
    toggleStyle = {},
    menuStyle = {},
    value,
    setValue,
    menuItems = [],
    variant = 'light',
    caption = '',
    clearable = false,
    icon = null,
    disabled = false,
    loading = false,
    closeOnSelect = true,
    clearFunction = () => console.warn('No clear function provided'),
}) => {
    const hasValue = value && value !== null;

    return (
        <div style={{ position: 'relative', width: '100%', ...containerStyle }}>
            <MDBDropdown style={{ width: '100%', ...dropdownStyle }} disabled={disabled}>
                <Toggle
                    disabled={disabled}
                    type="button"
                    color={variant}
                    clearable={clearable}
                    hasValue={hasValue}
                    style={{ border: '1px solid #ddd', ...toggleStyle }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                            {loading ? (
                                'Loading...'
                            ) : (
                                <>
                                    {icon}
                                    <span
                                        title={Array.isArray(value) && value.length > 1 ? value.join(', ') : undefined}
                                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                        {formatValue(value)}
                                    </span>
                                </>
                            )}
                        </div>
                        {caption ? <span style={{ fontSize: 10 }}>{caption}</span> : <></>}
                        {Array.isArray(value) && value.length > 1 && (
                            <div
                                style={{ fontSize: '0.75rem', color: '#666', marginTop: '2px', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                title={value.join(', ')}
                            >
                                {value.join(', ')}
                            </div>
                        )}
                    </div>
                </Toggle>
                <MenuContainer style={{ ...menuStyle }}>
                    {clearable ? (
                        <ClearButton onClick={() => { clearFunction(); }}>
                            <MDBIcon icon="ban" /> Clear Selection
                        </ClearButton>
                    ) : (
                        <></>
                    )}
                    {menuItems.map((item, i) => {
                        if (isSectionHeader(item)) {
                            return <SectionHeader key={i}>{formatValue(item)}</SectionHeader>;
                        }
                        const selectedItems = Array.isArray(value) ? value : typeof value === 'string' && value ? value.split(', ') : [];
                        const isSelected = selectedItems.includes(item);

                        return (
                            <MenuItem
                                key={i}
                                onClick={e => {
                                    if (!closeOnSelect) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }
                                    setValue(item);
                                }}
                                lastItem={menuItems.length - 1 === i}
                                className={isSelected ? 'active' : ''}
                            >
                                {formatValue(item)}
                            </MenuItem>
                        );
                    })}
                </MenuContainer>
            </MDBDropdown>
        </div>
    );
};

Dropdown.propTypes = {
    containerStyle: shape({}),
    dropdownStyle: shape({}),
    toggleStyle: shape({}),
    menuStyle: shape({}),
    value: oneOfType([string, arrayOf(string)]),
    setValue: func.isRequired,
    menuItems: arrayOf(string),
    variant: string,
    caption: string,
    clearable: bool,
    icon: element,
    disabled: bool,
    loading: bool,
    closeOnSelect: bool,
    clearFunction: func,
};

export default Dropdown;
